import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { PixelRatio, Pressable, StyleSheet, Text, View } from "react-native";
import { FieldLabel, type PassthroughFieldLabelProps } from "~/components/form/FieldLabel";
import { useTheme } from "~/providers/ThemeProvider";
import { setWidthOnLayout } from "~/util/layout";

export type RangeInputProps = {
    min: number;
    max: number;
    value?: number | null;
    step?: number;
    onChanged?: ((value: number | null) => void) | Dispatch<SetStateAction<number | null>>;
    onValueChange?: ((value: number) => void) | Dispatch<SetStateAction<number>>;
    disabled?: boolean;
} & PassthroughFieldLabelProps;

type StepData = {
    value: number;
    marked: boolean;
    first: boolean;
    last: boolean;
    selected: boolean;
};

export const RangeInput = ({
    min,
    max,
    step,
    value,
    onChanged,
    onValueChange,
    disabled,
    optional,
    ...labelProps
}: RangeInputProps) => {
    const { colours, background, text, border } = useTheme();
    const [width, setWidth] = useState<number>(0);
    const min_step = useMemo(() => (optional ? min - (step ?? 1) : min), [min, step, optional]);
    const zero_marker = useMemo(() => (min > 1 ? min : 0), [min]);
    const current = value ?? min_step;
    const inset = useMemo(() => PixelRatio.roundToNearestPixel(width * 0.05), [width]);
    const slider_width = useMemo(() => PixelRatio.roundToNearestPixel(width - 2 * inset), [width, inset]);

    const steps: StepData[] = useMemo(() => {
        const steps = max - zero_marker;
        const step_size = step ?? 1;
        const marked_step_size = Math.max(1, Math.floor(step_size));

        return [...Array.from({ length: steps }).keys()]
            .map((idx) => min + step_size * idx)
            .map((value, idx) => ({
                value,
                marked: value % marked_step_size === 0,
                first: idx === 0,
                last: value === max,
                selected: current >= value,
            }));
    }, [min, max, zero_marker, step, current]);

    const step_width = useMemo(
        () => PixelRatio.roundToNearestPixel(slider_width / steps.filter(({ marked }) => marked).length),
        [slider_width, steps],
    );

    const changeValue = (value: number) => {
        if (value !== current) {
            onChanged?.(value >= min ? value : null);
            onValueChange?.(value);
        }
    };

    const onLayout = setWidthOnLayout(setWidth);

    return (
        <FieldLabel {...labelProps} optional={optional}>
            <View style={[styles.container]}>
                <Pressable
                    style={[styles.endCap]}
                    disabled={disabled}
                    onPress={() => changeValue(zero_marker)}
                    hitSlop={{ left: 20 }}
                >
                    <View style={styles.marker}>
                        <Text style={[styles.text, text.secondary, styles.first]}>{zero_marker}</Text>
                    </View>
                </Pressable>
                <View style={[styles.border, border.tertiary, background.secondary]} onLayout={onLayout}>
                    {steps.map(({ value, marked, first, last, selected }) => (
                        <Pressable
                            key={`value-${value}`}
                            style={[
                                styles.selector,
                                first && styles.firstSelector,
                                last && styles.lastSelector,
                                {
                                    width: step_width,
                                    backgroundColor: selected
                                        ? disabled
                                            ? colours.background.tertiary
                                            : colours.icon.selected
                                        : colours.background.secondary,
                                },
                            ]}
                            disabled={disabled}
                            onPress={() => changeValue(value)}
                        >
                            {marked && (
                                <View
                                    style={[
                                        styles.marker,
                                        { backgroundColor: last ? "transparent" : colours.border.secondary },
                                    ]}
                                >
                                    <Text style={[styles.text, text.secondary, styles.middle]}>{value}</Text>
                                </View>
                            )}
                        </Pressable>
                    ))}
                </View>
                <View style={styles.endCap} />
            </View>
        </FieldLabel>
    );
};

const styles = StyleSheet.create({
    container: {
        height: 48,
        position: "relative",
        width: "100%",
        flexDirection: "row",
    },
    endCap: {
        width: 15,
        height: 32,
        position: "relative",
        justifyContent: "flex-end",
        alignItems: "flex-start",
        flexShrink: 0,
    },
    border: {
        position: "relative",
        height: 32,
        top: 0,
        left: 0,
        right: 0,
        bottom: 16,
        borderWidth: 1,
        borderRadius: 32,
        flexGrow: 1,
        flexShrink: 1,
        overflow: "visible",
        flexDirection: "row",
        alignItems: "flex-start",
    },
    indicator: {
        position: "absolute",
        height: 32,
        left: 0,
        top: 0,
        bottom: 0,
    },
    marker: {
        height: 32,
        width: 2,
        position: "absolute",
        right: 0,
    },
    first: {
        textAlign: "left",
        left: 0,
    },
    last: {
        textAlign: "right",
        right: 0,
    },
    middle: {
        textAlign: "center",
        right: -15,
    },
    text: {
        position: "absolute",
        top: 36,
        width: 32,
        fontSize: 12,
    },
    selector: {
        height: 30,
        flexGrow: 1,
        flexShrink: 1,
        position: "relative",
    },
    firstSelector: {
        borderTopLeftRadius: 24,
        borderBottomLeftRadius: 24,
        minWidth: 20,
    },
    lastSelector: {
        borderTopRightRadius: 24,
        borderBottomRightRadius: 24,
        minWidth: 20,
    },
});
